import { DeploymentUnitOutlined, ShopOutlined } from "@ant-design/icons";
import { Col, Divider, Row, Skeleton } from "antd";
import i18n from "i18next";
import type { FC } from "react";

import { PageLayout, PageTitle } from "../../../../components/AppLayout/components/Content/components";
import { SecondaryButton } from "../../../../components/Buttons/components/ActionButton";
import { NotificationType } from "../../../../components/NotificationAlerts";
import { NotificationAlert } from "../../../../components/NotificationAlerts/NotificationAlert";
import { Palette } from "../../../../core/styles/palette";
import { AcceptedCardTypesCard } from "../components/AcceptedCardTypesCard";
import { BaseStatisticCard } from "../components/BaseStatisticCard";
import { DataGroupInfoCard } from "../components/DataGroupInfoCard";
import { QuickLinksCard } from "../components/QuickLinksCard";
import { RecentActivityTimeline } from "../components/RecentActivityTimeline";
import { RecentCampaignsCard } from "../components/RecentCampaignsCard";
import { RecentJobExecutionsCard } from "../components/RecentJobExecutionsCard";
import { SystemHealthCard } from "../components/SystemHealthCard";
import { TerminalStatusCard } from "../components/TerminalStatusCard";
import { WeeklyCampaignActivityCard } from "../components/WeeklyCampaignActivityCard";
import { WelcomeCard } from "../components/WelcomeCard";
import { useGetDashboardQuery } from "../services/server/DashboardApi";
import {
    DashboardContainer,
    DashboardFlexBoxCol,
    DashboardFlexCardWrapper,
    DashboardHeroStatsRow,
    DashboardRow,
} from "../utils/dashboardStyles";

/**
 * DashboardPage - Main dashboard with improved layout and visualizations
 *
 * Features:
 * - Personalized welcome section
 * - Animated statistics with CountUp
 * - Interactive charts (Column, Pie)
 * - Recent campaigns grid with pagination
 * - Auto-refresh every 60 seconds
 */
export const DashboardPage: FC = () => {
    const { data, isLoading, isError, error, refetch } = useGetDashboardQuery(undefined, {
        pollingInterval: 60000, // Auto-refresh every 60 seconds
    });

    if (isLoading) {
        return (
            <DashboardContainer>
                <PageLayout name="dashboard_page" scrollable>
                    <PageTitle translationKey="menu.dashboard" />
                    <Divider />
                    <Skeleton active paragraph={{ rows: 10 }} />
                </PageLayout>
            </DashboardContainer>
        );
    }

    if (isError || !data) {
        return (
            <DashboardContainer>
                <PageLayout name="dashboard_page" scrollable>
                    <PageTitle translationKey="menu.dashboard" />
                    <Divider />
                    <NotificationAlert
                        description={i18n.t("dashboard.errorMessage")}
                        titleKey="dashboard.errorTitle"
                        type={NotificationType.ERROR}
                        action={<SecondaryButton onClick={() => refetch()}>{i18n.t("dashboard.retry")}</SecondaryButton>}
                        isCloseable={false}
                    />
                </PageLayout>
            </DashboardContainer>
        );
    }

    return (
        <DashboardContainer>
            <PageLayout name="dashboard_page" scrollable>
                <PageTitle translationKey="menu.dashboard" />
                <Divider />

                <Row gutter={[16, 16]}>
                    {/* Main Content Column */}
                    <Col xs={24} lg={18}>
                        {/* Hero Section with Welcome Card and Quick Stats */}
                        <DashboardRow gutter={[16, 16]}>
                            <Col xs={24} lg={18}>
                                <WelcomeCard
                                    applicationInfo={data.applicationInfo}
                                    campaignMetrics={data.campaignMetrics}
                                    orgUnitMetrics={data.orgUnitMetrics}
                                    terminalMetrics={data.terminalMetrics}
                                />
                            </Col>
                            <Col xs={24} lg={6}>
                                <DashboardHeroStatsRow gutter={[16, 16]}>
                                    <DashboardFlexBoxCol xs={12} lg={24}>
                                        <DashboardFlexCardWrapper>
                                            <BaseStatisticCard
                                                value={data.orgUnitMetrics.totalSitesCount}
                                                label="Sites"
                                                icon={<ShopOutlined />}
                                                color={Palette.DarkGreen}
                                            />
                                        </DashboardFlexCardWrapper>
                                    </DashboardFlexBoxCol>
                                    <DashboardFlexBoxCol xs={12} lg={24}>
                                        <DashboardFlexCardWrapper>
                                            <BaseStatisticCard
                                                value={data.terminalMetrics.activeTerminalsCount}
                                                label="Terminals"
                                                icon={<DeploymentUnitOutlined />}
                                                color={Palette.HeroGreen}
                                            />
                                        </DashboardFlexCardWrapper>
                                    </DashboardFlexBoxCol>
                                </DashboardHeroStatsRow>
                            </Col>
                        </DashboardRow>

                        {/* Charts Row - Weekly Activity and Terminal Status */}
                        <DashboardRow gutter={[16, 16]}>
                            <Col xs={24} lg={12}>
                                <WeeklyCampaignActivityCard data={data.weeklyCampaignActivity} />
                            </Col>
                            <Col xs={24} lg={12}>
                                <TerminalStatusCard data={data.terminalStatusData} />
                            </Col>
                        </DashboardRow>

                        {/* Charts Row - Accepted Card Types, System Health and Recent Job Executions */}
                        <DashboardRow gutter={[16, 16]}>
                            <Col xs={24} lg={14}>
                                <AcceptedCardTypesCard data={data.acceptedCardTypes} />
                            </Col>
                            <Col xs={24} lg={10}>
                                <Row gutter={[16, 16]}>
                                    <Col span={24}>
                                        <SystemHealthCard health={data.systemHealth} />
                                    </Col>
                                    <Col span={24}>
                                        <RecentJobExecutionsCard jobExecutions={data.systemHealth.jobExecutions} />
                                    </Col>
                                </Row>
                            </Col>
                        </DashboardRow>

                        {/* Recent Campaigns Grid */}
                        <Row gutter={[16, 16]}>
                            <Col xs={24}>
                                <RecentCampaignsCard campaigns={data.campaignMetrics.recentCampaigns} loading={isLoading} error={error} />
                            </Col>
                        </Row>
                    </Col>

                    {/* Sidebar Column */}
                    <Col xs={24} lg={6}>
                        <Row gutter={[16, 16]}>
                            {/* Data Group Info */}
                            <Col span={24}>
                                <DataGroupInfoCard dataGroupInfo={data.dataGroupInfo} />
                            </Col>

                            {/* Quick Links */}
                            <Col span={24}>
                                <QuickLinksCard />
                            </Col>

                            {/* Recent Activity Timeline */}
                            <Col span={24}>
                                <RecentActivityTimeline activity={data.recentActivity} />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </PageLayout>
        </DashboardContainer>
    );
};
